import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Icon, Tabs, Tag, message } from 'antd';

import {
  exportDB, getDeclaration, getStats, importDB, listDailyBalances, listEntryReviews,
} from './registerDB';
import {
  SITE_TYPE, buildCorrectionWorklist, buildDayWise, buildEntryList, dailyTargetKey,
  buildReconciliation, dayProgress, summariseReviews,
} from './reconEngine';
import {
  dayKeyFromMs, enumerateDays, periodKeyOf, scopeKeyOf,
} from './registerNormalize';
import { downloadReconciliationPdf } from './reconPdf';
import { unprintableToast } from '../../../../util/pdfText';
import { fmtL } from '../tableKit';

import BalancesPanel from './BalancesPanel';
import DailyBalancePanel from './DailyBalancePanel';
import EntryReviewPanel from './EntryReviewPanel';

const { TabPane } = Tabs;

/*
 * Fuel register reconciliation.
 *
 *   1  Balances     enter the register's closing balance per tank (or the issued
 *                   total where there is no tank). Matches -> done.
 *   2  Day-wise     only if it does not match. One number a day, newest first,
 *                   until you reach the last day the two sides agreed.
 *   3  Entries      from that day on, mark each AssetPro entry off against the
 *                   register or record what it should say.
 *
 * Corrections flow forward, so fixing an entry days back reconciles the period —
 * and that is shown, not left to be worked out.
 *
 * No API calls: every AssetPro figure comes from what the parent report already
 * fetched. Nothing is written back to AssetPro.
 */
function RegisterReconciliationTab({ ctx, onClearConsumerFilters }) {
  const [dbVersion, setDbVersion] = useState(0);
  const [inner, setInner] = useState('balances');
  const [dateFocus, setDateFocus] = useState(null);
  const [targetFocus, setTargetFocus] = useState(null);
  // Which target the Day-wise tab should open on, so "find the day it broke" on a
  // direct-fuel difference does not land the reviewer on a tank.
  const [dailyFocus, setDailyFocus] = useState(null);
  const refresh = useCallback(() => setDbVersion((v) => v + 1), []);

  const hasFetched = !!(ctx && ctx.queriedRange);

  const periodFrom = hasFetched ? ctx.queriedRange[0].format('YYYY-MM-DD') : null;
  const periodTo = hasFetched ? ctx.queriedRange[1].format('YYYY-MM-DD') : null;
  const periodKey = hasFetched ? periodKeyOf(ctx.queriedRange[0], ctx.queriedRange[1]) : '';

  const f = (ctx && ctx.filters) || {};
  const scopeKey = useMemo(
    () => scopeKeyOf({
      fuelType: f.fFuelType,
      locations: f.fLocation || [],
      subLocations: f.fSubLocation || [],
      storages: f.fStorage || [],
    }),
    [f.fFuelType, f.fLocation, f.fSubLocation, f.fStorage]
  );

  // ---- AssetPro side, scoped ----------------------------------------------
  // Sub-location is recorded on a fuel ENTRY, so it can narrow issues and direct
  // purchases. It cannot narrow a tank's closing balance — that is a physical
  // stock level and does not divide by client. Where a tank is in play and a
  // sub-location filter is set, the tab says so rather than quietly comparing a
  // narrowed figure against a whole one.
  const scoped = useMemo(() => {
    if (!hasFetched) return { storages: [], apIssues: [], apReceipts: [], apDirect: [], tankRecords: [], unattributableTanks: [] };
    // A tank is allocated to a location AND a sub-location, so a sub-location
    // filter is a valid scope for its closing balance.
    //
    // A tank with no sub-location recorded cannot be attributed to one, so it is
    // EXCLUDED while such a filter is on and reported — including it would put a
    // whole tank's stock behind a filter it may not belong to.
    const subFilter = f.fSubLocation || [];
    const allStorages = ctx.filteredStorages || [];
    const storages = subFilter.length
      ? allStorages.filter((st) => subFilter.includes(st.subLocation))
      : allStorages;
    const unattributableTanks = subFilter.length
      ? allStorages.filter((st) => !st.subLocationId)
      : [];
    const storageIds = new Set(storages.map((s) => s.storageId));
    const matchFuel = (ft) => !f.fFuelType || ft === f.fFuelType;
    const matchSub = (sl) => !(f.fSubLocation || []).length || f.fSubLocation.includes(sl);
    const fromMs = ctx.queriedRange[0].valueOf();
    const toMs = ctx.queriedRange[1].valueOf();

    const apIssues = (ctx.issueRows || [])
      .filter((r) => storageIds.has(r.storageId))
      .filter((r) => matchFuel(r.fuelType))
      .filter((r) => matchSub(r.subLocation))
      .map((r) => ({ ...r, dayKey: dayKeyFromMs(r.datetimeMs) }));

    const apReceipts = (ctx.receiptRows || [])
      .filter((r) => storageIds.has(r.storageId))
      .filter((r) => matchFuel(r.fuelType))
      .map((r) => ({ ...r, dayKey: dayKeyFromMs(r.datetimeMs) }));

    const apDirect = (ctx.mileageRows || [])
      .filter((r) => r.isMarket === true || r.source === 'MARKET')
      .filter((r) => !(ctx.allStorageIds || new Set()).has(r.assetId))
      .filter((r) => r.datetimeMs >= fromMs && r.datetimeMs <= toMs)
      .filter((r) => matchFuel(r.fuelType))
      .filter((r) => !(f.fLocation || []).length || f.fLocation.includes(r.locationName))
      .filter((r) => matchSub(r.subLocation))
      .map((r) => ({ ...r, dayKey: dayKeyFromMs(r.datetimeMs) }));

    // Every movement through the in-scope tanks, narrowed ONLY by tank. The
    // running balance covers everything that passed through, so building the
    // chain from a sub-location-narrowed set would give the wrong closing.
    // Tagged, because the normalised rows carry only a quantity — without the
    // direction a receipt would be subtracted from the tank like an issue.
    const tankRecords = (ctx.issueRows || [])
      .filter((r) => storageIds.has(r.storageId))
      .map((r) => ({ ...r, kind: 'ISSUE', dayKey: dayKeyFromMs(r.datetimeMs) }))
      .concat(
        (ctx.receiptRows || [])
          .filter((r) => storageIds.has(r.storageId))
          .map((r) => ({ ...r, kind: 'RECEIPT', dayKey: dayKeyFromMs(r.datetimeMs) }))
      );

    return { storages, apIssues, apReceipts, apDirect, tankRecords, unattributableTanks };
  }, [hasFetched, ctx, f.fFuelType, f.fSubLocation, f.fLocation]);

  const days = useMemo(
    () => (hasFetched ? enumerateDays(periodFrom, periodTo) : []),
    [hasFetched, periodFrom, periodTo]
  );

  // ---- stored register data ------------------------------------------------
  const stored = useMemo(() => ({
    declaration: getDeclaration(periodKey, scopeKey) || {},
    dailyBalances: listDailyBalances({ periodKey }),
    reviews: listEntryReviews({ periodKey }),
    stats: getStats(),
    // eslint-disable-next-line
  }), [periodKey, scopeKey, dbVersion]);

  // ---- the reconciliation --------------------------------------------------
  const recon = useMemo(
    () => buildReconciliation({
      storages: scoped.storages,
      apIssues: scoped.apIssues,
      apReceipts: scoped.apReceipts,
      apDirect: scoped.apDirect,
      tankRecords: scoped.tankRecords,
      days,
      declaration: stored.declaration,
      reviews: stored.reviews,
      periodFrom,
      periodTo,
    }),
    [scoped, days, stored.declaration, stored.reviews, periodFrom, periodTo]
  );

  const entries = useMemo(
    () => buildEntryList({ siteType: recon.siteType, ...scoped }),
    [recon.siteType, scoped]
  );

  const summary = useMemo(
    () => summariseReviews({ entries, reviews: stored.reviews }),
    [entries, stored.reviews]
  );

  const worklist = useMemo(
    () => buildCorrectionWorklist({ summary, periodFrom, periodTo }),
    [summary, periodFrom, periodTo]
  );

  // Day-wise is built per target, on demand, so a multi-tank site does not
  // compute every tank's daily series until it is looked at.
  const dayWiseFor = useCallback((target) => buildDayWise({
    days,
    siteType: recon.siteType,
    storage: target.kind === 'TANK_CLOSING'
      ? scoped.storages.find((s) => s.storageId === target.storageId)
      : null,
    // A tank is a balance; ISSUED and DIRECT are daily flows.
    flowKind: target.kind === 'TANK_CLOSING' ? null : target.kind,
    apIssues: scoped.apIssues,
    apReceipts: scoped.apReceipts,
    apDirect: scoped.apDirect,
    tankRecords: scoped.tankRecords,
    dailyRows: stored.dailyBalances.filter((d) => d.target === dailyTargetKey(target)),
    reviews: stored.reviews,
    periodFrom,
    periodTo,
  }), [days, recon.siteType, scoped, stored.dailyBalances, stored.reviews, periodFrom, periodTo]);

  const goToEntries = useCallback((date, targetKey) => {
    setDateFocus(date);
    setTargetFocus(targetKey || null);
    setInner('entries');
  }, []);

  // The report for the record. Day-wise is included only for targets the
  // reviewer actually filled in — printing thirty blank rows would say nothing.
  const assetChoices = useMemo(() => {
    const byId = (ctx.refData && ctx.refData.assetById) || {};
    return Object.keys(byId)
      .map((id) => ({ id, assetCode: byId[id].assetCode || '', altName: byId[id].altName || byId[id].description || '' }))
      .filter((a) => a.assetCode)
      .sort((a, b) => a.assetCode.localeCompare(b.assetCode));
  }, [ctx.refData]);

  const focusProgress = useMemo(() => {
    if (!dateFocus) return null;
    const target = recon.targets.find((t) => t.key === targetFocus) || recon.targets[0];
    if (!target) return null;
    const dw = dayWiseFor(target);
    const row = dw.chronological.find((r) => r.date === dateFocus);
    const p = dayProgress(row);
    return p ? { ...p, targetLabel: target.label, targetKind: target.kind } : null;
  }, [dateFocus, targetFocus, recon.targets, dayWiseFor]);

  const printReport = useCallback(() => {
    const dayWiseByTarget = {};
    recon.targets.forEach((t) => {
      const dw = dayWiseFor(t);
      if (dw.entered) dayWiseByTarget[t.key] = dw;
    });
    const { unprintable } = downloadReconciliationPdf({
      recon,
      summary,
      worklist,
      dayWiseByTarget,
      periodFrom,
      periodTo,
      filters: f,
    });
    if (unprintable > 0) message.warning(`Reconciliation report downloaded. ${unprintableToast(unprintable)}`, 8);
    else message.success('Reconciliation report downloaded.');
  }, [recon, summary, worklist, dayWiseFor, periodFrom, periodTo, f]);

  const handleImport = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    importDB(file)
      .then((counts) => {
        const parts = Object.keys(counts).filter((k) => counts[k]).map((k) => `${counts[k]} ${k}`);
        message.success(`Merged ${parts.join(', ') || 'nothing new'}.`);
        refresh();
      })
      .catch((err) => message.error(`Import failed: ${err.message}`));
  }, [refresh]);

  if (!hasFetched) {
    return (
      <Alert
        type="info"
        showIcon
        message="Fetch the report first"
        description="Pick a period and press Fetch. The reconciliation reuses the data the report has already loaded — it makes no API calls of its own."
      />
    );
  }

  const unattributable = scoped.unattributableTanks || [];

  return (
    <div>
      <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: '10px 14px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600, marginRight: 4 }}>Reconciling</span>
          <Tag color="blue">{periodFrom} → {periodTo}</Tag>
          <Tag>{f.fFuelType || 'All fuel types'}</Tag>
          <Tag>{(f.fLocation || []).length ? f.fLocation.join(', ') : 'All locations'}</Tag>
          {(f.fSubLocation || []).length > 0 && <Tag>{f.fSubLocation.join(', ')}</Tag>}
          <span style={{ flex: 1 }} />
          <Button
            size="small"
            type={recon.state === 'RECONCILED' ? 'primary' : 'default'}
            icon="file-pdf"
            onClick={printReport}
          >
            Print report
          </Button>
          <Button size="small" icon="download" onClick={() => exportDB()}>Export</Button>
          <label style={{ marginBottom: 0 }}>
            <input type="file" accept="application/json" style={{ display: 'none' }} onChange={handleImport} />
            <span className="ant-btn ant-btn-sm" style={{ cursor: 'pointer' }}><Icon type="upload" /> Import</span>
          </label>
        </div>
      </Card>

      {unattributable.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${unattributable.length} tank(s) have no sub-location recorded and are excluded`}
          description={`${unattributable.map((t) => t.assetCode).join(', ')} — the asset carries no sub-location, so it cannot be attributed to the one filtered. Including it would put a whole tank's stock behind a filter it may not belong to. Set the sub-location on the asset in AssetPro, or clear the filter to reconcile it.`}
        />
      )}

      {(f.fAssetType || []).length + (f.fAsset || []).length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Asset filters are ignored here"
          description="They select the receiving machine, and a register balance has no machine dimension. Narrowing one side and not the other would produce a difference that is confidently wrong."
        />
      )}

      {recon.dataIssues && recon.dataIssues.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="AssetPro's own figures disagree with each other"
          description={
            <div>
              {recon.dataIssues.map((d) => (
                <div key={d.key} style={{ marginBottom: 6 }}>
                  <b>{d.title}</b>
                  <div style={{ color: '#595959' }}>{d.detail}</div>
                </div>
              ))}
              <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>
                The closing balance used below is the one the app's own Bowser Day-wise Report ends
                on, so the two agree. Nothing has been picked silently — the other figures are named
                above so the gap can be raised with whoever owns the data.
              </div>
            </div>
          }
        />
      )}

      <Tabs activeKey={inner} onChange={setInner} animated={false}>
        <TabPane
          tab={
            <span>
              Balances{' '}
              {recon.state === 'RECONCILED' && <Badge status="success" />}
              {recon.state === 'DIFFERS' && <Badge status="error" />}
            </span>
          }
          key="balances"
        >
          <BalancesPanel
            recon={recon}
            periodKey={periodKey}
            scopeKey={scopeKey}
            periodTo={periodTo}
            scopeNote={[
              (f.fLocation || []).length ? f.fLocation.join(', ') : null,
              (f.fSubLocation || []).length ? f.fSubLocation.join(', ') : null,
            ].filter(Boolean).join(' · ') || null}
            onChanged={refresh}
            onGoToDaily={(key) => { setDailyFocus(key || null); setInner('daily'); }}
            onGoToEntries={goToEntries}
          />
        </TabPane>

        <TabPane tab="Day-wise" key="daily">
          <DailyBalancePanel
            recon={recon}
            dayWiseFor={dayWiseFor}
            targetFocus={dailyFocus}
            periodKey={periodKey}
            scopeKey={scopeKey}
            periodTo={periodTo}
            onChanged={refresh}
            onGoToEntries={goToEntries}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              Entries{' '}
              {summary.counts.unreviewed
                ? <Badge count={summary.counts.unreviewed} style={{ backgroundColor: '#faad14' }} />
                : <Badge status="success" />}
            </span>
          }
          key="entries"
        >
          <EntryReviewPanel
            entries={entries}
            summary={summary}
            worklist={worklist}
            recon={recon}
            periodKey={periodKey}
            scopeKey={scopeKey}
            periodFrom={periodFrom}
            periodTo={periodTo}
            storages={scoped.storages}
            assetChoices={assetChoices}
            directCount={scoped.apDirect.length}
            dateFocus={dateFocus}
            kindFocus={focusProgress ? focusProgress.targetKind : null}
            focusProgress={focusProgress}
            onChanged={refresh}
          />
        </TabPane>
      </Tabs>

      <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
        AssetPro in this scope: {scoped.apIssues.length} issue(s)
        {scoped.storages.length > 0 && `, ${scoped.apReceipts.length} receipt(s)`}
        , {scoped.apDirect.length} direct — {fmtL(
          scoped.apIssues.concat(scoped.apDirect).reduce((s, r) => s + (Number(r.quantity) || 0), 0)
        )} L to machines.
      </div>
    </div>
  );
}

export default RegisterReconciliationTab;
